import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Menu,
  X,
  Zap,
  Wallet,
  ArrowRight,
  BriefcaseBusiness,
  Receipt,
  Building2,
  Boxes,
  ClipboardCheck,
  Database,
  Users,
  Activity,
  LogOut,
} from "lucide-react";

import { Toaster, toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import MobileBottomNav from "@/components/MobileBottomNav";
import Hero from "@/components/Hero";
import KpiGrid from "@/components/KpiGrid";
import ResourceCard from "@/components/ResourceCard";
import CategoryFilter from "@/components/CategoryFilter";
import StatsCharts from "@/components/StatsCharts";
import ActivityFeed from "@/components/ActivityFeed";
import ExpenseSummaryCards from "@/components/expenses/ExpenseSummaryCards";

import {
  fetchResources,
  fetchStats,
  fetchActivity,
  logActivity,
  toggleStar,
} from "@/lib/api";

import { expenseApi } from "@/lib/expenseApi";
import { authApi } from "@/lib/adminApi";
import { normalizeRole } from "@/components/admin/AdminRoute";
import { useTheme } from "@/lib/theme";

export default function DashboardPage() {
  const { theme, toggle } = useTheme();

  const navigate = useNavigate();
  const location = useLocation();

  /* ============================================================
     CURRENT USER
     ============================================================ */

  const currentUser = authApi.getCachedUser();

  const displayName =
    currentUser?.name ||
    currentUser?.full_name ||
    currentUser?.fullName ||
    currentUser?.username ||
    currentUser?.email ||
    "User";

  const userRole = normalizeRole(currentUser?.role);

  /* ============================================================
     ROLE BASED DASHBOARD TITLE
     ============================================================ */

  const dashboardTitleMap = {
    ADMIN: "Dashboard",
    PM: "Dashboard",
    APM: "Dashboard",
    FIELD_OFFICER: "Dashboard",
    CIRCLE_INCHARGE: "Dashboard",
  };

  const dashboardTitle =
    dashboardTitleMap[userRole] || "Dashboard";

  const isAdmin = userRole === "ADMIN";

  /* ============================================================
     STATE
     ============================================================ */

  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);

  const [query, setQuery] = useState("");

  const [category, setCategory] = useState(
    () => location.state?.category || "All"
  );

  const [loading, setLoading] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  /* ============================================================
     LOAD DASHBOARD DATA
     ============================================================ */

  const load = async () => {
    try {
      const [r, s, a, es] = await Promise.all([
        fetchResources(),
        fetchStats(),
        fetchActivity(8),
        expenseApi.summary().catch(() => null),
      ]);

      setResources(r);
      setStats(s);
      setActivity(a);
      setExpenseSummary(es);
    } catch (e) {
      console.error(e);
      toast.error("Could not load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ============================================================
     CATEGORY FROM NAVIGATION STATE
     ============================================================ */

  useEffect(() => {
    if (location.state?.category) {
      setCategory(location.state.category);

      window.history.replaceState({}, "");
    }
  }, [location.state]);

  /* ============================================================
     RESOURCE COUNTS
     ============================================================ */

  const counts = useMemo(() => {
    const c = {};

    resources.forEach((r) => {
      c[r.category] = (c[r.category] || 0) + 1;
    });

    return c;
  }, [resources]);

  /* ============================================================
     FILTERED RESOURCES
     ============================================================ */

  const filtered = useMemo(() => {
    return resources.filter((r) => {
      if (
        category !== "All" &&
        r.category !== category
      ) {
        return false;
      }

      if (query) {
        const q = query.toLowerCase();

        return (
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q)
        );
      }

      return true;
    });
  }, [resources, category, query]);

  /* ============================================================
     STARRED RESOURCES
     ============================================================ */

  const starredResources = useMemo(
    () =>
      resources
        .filter((r) => r.starred)
        .slice(0, 6),
    [resources]
  );

  /* ============================================================
     OPEN RESOURCE
     ============================================================ */

  const handleOpen = async (r) => {
    try {
      await logActivity(r.id, "opened");

      const [a, s] = await Promise.all([
        fetchActivity(8),
        fetchStats(),
      ]);

      setActivity(a);
      setStats(s);
    } catch (e) {
      /* silent */
    }
  };

  /* ============================================================
     STAR / UNSTAR RESOURCE
     ============================================================ */

  const handleToggleStar = async (r) => {
    try {
      const updated = await toggleStar(
        r.id,
        !r.starred
      );

      setResources((prev) =>
        prev.map((x) =>
          x.id === r.id ? updated : x
        )
      );

      const s = await fetchStats();
      setStats(s);

      toast.success(
        updated.starred
          ? "Starred"
          : "Removed star",
        {
          description: updated.title,
        }
      );
    } catch (e) {
      toast.error("Update failed");
    }
  };

  /* ============================================================
     CATEGORY SELECTION
     ============================================================ */

  const handleSelectCategory = (c) => {
    setCategory(c);
    setMobileNav(false);

    if (c === "All") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    document
      .getElementById("resources-section")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  /* ============================================================
     ADMIN ONLY MODULES
     ============================================================ */

  const openAdminOnly = (path, moduleName) => {
    if (!isAdmin) {
      toast.error("Admin access required", {
        description: `${moduleName} is available only to ADMIN.`,
      });

      return;
    }

    navigate(path);
  };

  /* ============================================================
     LOGOUT
     ============================================================ */

  const handleLogout = async () => {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);

    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      authApi.clearSession();

      navigate("/admin/login", {
        replace: true,
      });
    }
  };

  /* ============================================================
     PAGE
     ============================================================ */

  return (
    <div
      data-testid="dashboard-page"
      className="min-h-screen flex bg-background text-foreground"
    >
      {/* ========================================================
          MAIN SIDEBAR
          ======================================================== */}

      <Sidebar
        active={category}
        onSelect={handleSelectCategory}
        theme={theme}
        onToggleTheme={toggle}
        counts={counts}
      />

      {/* ========================================================
          MOBILE NAV OVERLAY
          ======================================================== */}

      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[280px] bg-background border-r border-border">
            <Sidebar
              mobileOverlay
              active={category}
              onSelect={handleSelectCategory}
              theme={theme}
              onToggleTheme={toggle}
              counts={counts}
              onClose={() => setMobileNav(false)}
            />
          </div>

          <div
            className="flex-1 bg-background/60"
            onClick={() => setMobileNav(false)}
          />
        </div>
      )}

      {/* ========================================================
          MAIN CONTENT
          ======================================================== */}

      <main className="flex-1 min-w-0">

        {/* ======================================================
            OFFICIAL HOME HEADER
            LEFT  = DASHBOARD
            RIGHT = USER
            ROLE + LOGOUT SAME LINE
            ====================================================== */}

        <div className="sticky top-0 z-30 glass glass-dark dark:glass-dark [.light_&]:glass-light border-b border-border">
          <div className="px-4 sm:px-8 min-h-16 flex items-center gap-4">

            {/* MOBILE MENU */}

            <button
              data-testid="mobile-menu"
              type="button"
              className="lg:hidden w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0"
              onClick={() =>
                setMobileNav((v) => !v)
              }
              aria-label="Menu"
            >
              {mobileNav ? (
                <X className="w-4 h-4" />
              ) : (
                <Menu className="w-4 h-4" />
              )}
            </button>

            {/* =================================================
                LEFT — DASHBOARD NAME
                ================================================= */}

            <div className="min-w-0 flex-1">
              <div
                className="
                  truncate
                  text-[15px]
                  sm:text-base
                  md:text-lg
                  font-heading
                  font-black
                  uppercase
                  tracking-[0.04em]
                  text-foreground
                "
                title={dashboardTitle}
              >
                {dashboardTitle}
              </div>
            </div>

            {/* =================================================
                RIGHT — USER + ROLE + LOGOUT
                ================================================= */}

            <div className="flex flex-col items-end justify-center min-w-0 shrink-0">

              {/* USER NAME */}

              <div
                className="
                  truncate
                  max-w-[280px]
                  sm:max-w-[360px]
                  text-xs
                  sm:text-sm
                  font-serif
                  font-semibold
                  tracking-wide
                  uppercase
                  text-foreground
                  leading-tight
                "
                title={`Welcome Mr. ${displayName}`}
              >
                WELCOME MR. {displayName}
              </div>

              {/* ROLE + LOGOUT */}

              <div className="flex items-center gap-2 mt-0.5">

                {/* ROLE */}

                <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                  {userRole || "USER"}
                </span>

                {/* LOGOUT */}

                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  title="Logout"
                  aria-label="Logout"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-1
                    h-6
                    px-2
                    rounded-md
                    border
                    border-border
                    bg-background
                    hover:bg-muted
                    text-[10px]
                    font-semibold
                    transition-colors
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <LogOut className="w-3 h-3" />

                  <span className="hidden sm:inline">
                    {loggingOut
                      ? "Logging out..."
                      : "Logout"}
                  </span>
                </button>

              </div>
            </div>

          </div>
        </div>

        {/* ======================================================
            DASHBOARD BODY
            ====================================================== */}

        <div className="px-4 sm:px-8 py-8 space-y-8 max-w-[1600px]">

          {/* ====================================================
              HERO
              ==================================================== */}

          <Hero
            total={stats?.total_resources || 0}
            categories={stats?.categories || 0}
          />

          {/* ====================================================
              MAIN WEBSITE MODULES
              ==================================================== */}

          <section data-testid="main-website-modules">

            <div className="mb-5">

              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Main Website
              </div>

              <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-1">
                All Modules
              </h2>

              <p className="text-sm text-muted-foreground mt-1">
                All modules are visible. Actions and
                restricted access are controlled by the
                logged-in user role.
              </p>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

              {/* =================================================
                  DAILY EXPENSES
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/expenses")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Wallet className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  Daily Expenses
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Expenses, entries and reports
                </p>
              </button>

              {/* =================================================
                  HRMS & PAYROLL
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/hrms")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <BriefcaseBusiness className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  HRMS & Payroll
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Employees, attendance, leaves and payroll
                </p>
              </button>

              {/* =================================================
                  VENDOR BILLING
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/billing")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Receipt className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  Vendor Billing
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Vendor bills and billing records
                </p>
              </button>

              {/* =================================================
                  DISCOM
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/discom")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Building2 className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  DISCOM
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  DISCOM operations and records
                </p>
              </button>

              {/* =================================================
                  INVENTORY
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/inventory")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Boxes className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  Inventory
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Stock, materials and inventory
                </p>
              </button>

              {/* =================================================
                  DT INSPECTION
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  navigate("/dt-inspection")
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <ClipboardCheck className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  DT Inspection
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  DT inspection and field records
                </p>
              </button>

              {/* =================================================
                  MASTER DATA
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  openAdminOnly(
                    "/admin/resources",
                    "Master Data"
                  )
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Database className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  Master Data
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  {isAdmin
                    ? "Manage master resources"
                    : "Visible to all • ADMIN access only"}
                </p>
              </button>

              {/* =================================================
                  USER MANAGEMENT
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  openAdminOnly(
                    "/admin/users",
                    "User Management"
                  )
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Users className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  User Management
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  {isAdmin
                    ? "Manage users and roles"
                    : "Visible to all • ADMIN access only"}
                </p>
              </button>

              {/* =================================================
                  ACTIVITY LOG
                  ================================================= */}

              <button
                type="button"
                onClick={() =>
                  openAdminOnly(
                    "/admin/activity",
                    "Activity Log"
                  )
                }
                className="
                  group
                  text-left
                  rounded-2xl
                  border
                  border-border
                  bg-card
                  p-5
                  hover:border-primary/50
                  hover:shadow-md
                  transition-all
                "
              >
                <Activity className="w-6 h-6 mb-4 text-primary" />

                <h3 className="font-heading font-bold text-lg">
                  Activity Log
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  {isAdmin
                    ? "View system activity"
                    : "Visible to all • ADMIN access only"}
                </p>
              </button>

            </div>
          </section>

          {/* ====================================================
              DAILY EXPENSES SNAPSHOT
              ==================================================== */}

          {expenseSummary && (
            <section data-testid="expenses-snapshot">

              <div className="mb-4 flex items-end justify-between flex-wrap gap-2">

                <div>

                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                    Finance
                  </div>

                  <h2 className="font-heading text-2xl font-bold mt-1 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[hsl(var(--primary))]" />
                    Daily Expenses
                  </h2>

                </div>

                <button
                  data-testid="open-expenses-module"
                  onClick={() =>
                    navigate("/expenses")
                  }
                  className="
                    inline-flex
                    items-center
                    gap-1.5
                    px-4
                    py-2
                    rounded-full
                    bg-foreground
                    text-background
                    text-sm
                    font-semibold
                    hover:scale-[0.98]
                    transition-transform
                  "
                >
                  Open module

                  <ArrowRight className="w-4 h-4" />
                </button>

              </div>

              <ExpenseSummaryCards
                summary={expenseSummary}
                compact
              />

            </section>
          )}

          {/* ====================================================
              KPIs
              ==================================================== */}

          <section>

            <div className="mb-4 flex items-baseline justify-between">

              <div>

                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Snapshot
                </div>

                <h2 className="font-heading text-2xl font-bold mt-1">
                  At a glance
                </h2>

              </div>

            </div>

            <KpiGrid stats={stats} />

          </section>

          {/* ====================================================
              CHARTS + ACTIVITY
              ==================================================== */}

          <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">

            <div className="xl:col-span-3">
              <StatsCharts stats={stats} />
            </div>

            <div className="xl:col-span-1">
              <ActivityFeed items={activity} />
            </div>

          </section>

          {/* ====================================================
              STARRED QUICK ACCESS
              ==================================================== */}

          {starredResources.length > 0 && (
            <section data-testid="starred-section">

              <div className="mb-4 flex items-baseline justify-between">

                <div>

                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                    Pinned
                  </div>

                  <h2 className="font-heading text-2xl font-bold mt-1">
                    Quick access
                  </h2>

                </div>

                <span className="text-xs text-muted-foreground">
                  {starredResources.length} starred
                </span>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                {starredResources.map((r) => (
                  <ResourceCard
                    key={r.id}
                    resource={r}
                    onOpen={handleOpen}
                    onToggleStar={handleToggleStar}
                  />
                ))}

              </div>

            </section>
          )}

          {/* ====================================================
              RESOURCES
              ==================================================== */}

          <section id="resources-section">

            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">

              <div>

                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Library
                </div>

                <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-1">
                  {category === "All"
                    ? "All resources"
                    : category}
                </h2>

                <p className="text-sm text-muted-foreground mt-1">
                  {filtered.length} of {resources.length} · every asset one tap away
                </p>

              </div>

              <CategoryFilter
                active={category}
                onChange={setCategory}
                counts={counts}
              />

            </div>

            {/* ==================================================
                LOADING
                ================================================== */}

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                {Array.from({ length: 8 }).map(
                  (_, i) => (
                    <div
                      key={i}
                      className="
                        rounded-xl
                        border
                        border-border
                        bg-card
                        p-5
                        h-52
                        animate-pulse
                      "
                    />
                  )
                )}

              </div>

            ) : filtered.length === 0 ? (

              /* ==================================================
                 NO RESULTS
                 ================================================== */

              <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">

                <div className="w-12 h-12 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">

                  <Zap className="w-5 h-5 text-muted-foreground" />

                </div>

                <div className="font-heading font-bold text-lg">
                  No matches
                </div>

                <p className="text-sm text-muted-foreground mt-1">
                  Try a different search or category.
                </p>

              </div>

            ) : (

              /* ==================================================
                 RESOURCE GRID
                 ================================================== */

              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  show: {
                    transition: {
                      staggerChildren: 0.04,
                    },
                  },
                }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              >

                {filtered.map((r) => (
                  <ResourceCard
                    key={r.id}
                    resource={r}
                    onOpen={handleOpen}
                    onToggleStar={handleToggleStar}
                  />
                ))}

              </motion.div>
            )}

          </section>

          {/* ====================================================
              FOOTER
              ==================================================== */}

          <footer className="pt-6 pb-10 border-t border-border flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">

            <div>
              © 2026 Prathvi Power Solutions · Master Dashboard
            </div>

            <div className="flex items-center gap-2">

              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />

              All grid nodes reporting

            </div>

          </footer>

        </div>
      </main>

      {/* ========================================================
          MOBILE BOTTOM NAV
          ======================================================== */}

      <MobileBottomNav />

      {/* ========================================================
          TOASTER
          ======================================================== */}

      <Toaster
        position="top-right"
        theme={theme}
        toastOptions={{
          style: {
            background: "hsl(var(--card))",
            color: "hsl(var(--foreground))",
            border: "1px solid hsl(var(--border))",
          },
        }}
      />

    </div>
  );
}